/**
 * Catálogo de servicios.
 *
 * Cambiar un precio acá **no toca el pasado**: cada cobro emitido guarda su
 * propio precio congelado. Lo dice la pantalla, porque es la duda que aparece
 * siempre que alguien va a subir una tarifa.
 */

import { useState } from 'react';
import { formatMoney, parseMoneyInput } from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import {
  Async, Badge, Button, Card, Cell, Empty, Field, Modal, Money, Note, PageHead, Table,
} from '@/components/ui';

interface Service {
  id: string; code: string; name: string; description: string | null;
  base_price: string; base_price_minor: number; features_json: string;
  default_tax_rate: number; is_active: number;
}

/** `features_json` es texto libre en la base — acá se lee con cuidado. */
function parseFeatures(featuresJson: string): string[] {
  try {
    const parsed = JSON.parse(featuresJson || '[]');
    return Array.isArray(parsed) ? parsed.filter((f): f is string => typeof f === 'string') : [];
  } catch {
    return [];
  }
}

export function CatalogPage() {
  const query = useApi<{ services: Service[] }>('/admin/services');
  const [editing, setEditing] = useState<Service | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHead
        title="Catálogo"
        subtitle="Los paquetes que se contratan. El precio de acá es el punto de partida; un consultorio puede tener el suyo."
        actions={<Button variant="primary" onClick={() => setCreating(true)}>Nuevo servicio</Button>}
      />

      <Async query={query} empty={(d) => d.services.length === 0 && (
        <Empty icon="catalogo" title="Todavía no hay servicios" hint="Cargá los paquetes que ofrece Clean Blue." />
      )}>
        {(data) => (
          <Card flush>
            <Table head={
              <tr><th>Código</th><th>Servicio</th><th className="right">Precio</th><th className="right">IVA</th><th /></tr>
            }>
              {data.services.map((s) => (
                <tr key={s.id}>
                  <td className="mono">{s.code}</td>
                  <td className="strong">
                    <Cell sub={s.description}>
                      {s.name}
                      {!s.is_active && <Badge tone="neutral"> inactivo</Badge>}
                    </Cell>
                    {parseFeatures(s.features_json).length > 0 && (
                      <div className="mt-1">
                        {parseFeatures(s.features_json).slice(0, 4).map((f) => (
                          <Badge key={f} tone="neutral" plain>{f}</Badge>
                        ))}
                        {parseFeatures(s.features_json).length > 4 && (
                          <span className="muted small"> y {parseFeatures(s.features_json).length - 4} más</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="right"><Money value={s.base_price_minor} /></td>
                  <td className="right muted">{(s.default_tax_rate * 100).toFixed(0)}%</td>
                  <td className="right"><Button onClick={() => setEditing(s)}>Editar</Button></td>
                </tr>
              ))}
            </Table>
          </Card>
        )}
      </Async>

      {(editing || creating) && (
        <ServiceModal
          service={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
        />
      )}
    </>
  );
}

function ServiceModal({ service, onClose }: { service: Service | null; onClose: () => void }) {
  const [form, setForm] = useState({
    code: service?.code ?? '',
    name: service?.name ?? '',
    // En colones, como todos los campos de dinero del sistema (Finanzas,
    // Obligaciones): quien edita un precio piensa en ₡65.000, no en 6500000
    // céntimos. El servidor sigue guardando céntimos — eso no cambia.
    price: service ? formatMoney(service.base_price_minor, { symbol: false }) : '',
    // El IVA se edita como el número que la gente dice en voz alta (13, no
    // 0.13). `default_tax_rate` en la base sigue siendo la fracción.
    tax_rate_pct: String(Math.round((service?.default_tax_rate ?? 0.13) * 100)),
  });

  const priceMinor = form.price ? parseMoneyInput(form.price) : null;
  const taxRate = form.tax_rate_pct.trim() === '' ? null : Number(form.tax_rate_pct) / 100;
  const taxRateValid = taxRate != null && Number.isFinite(taxRate) && taxRate >= 0 && taxRate < 1;

  /* Descripción y características se dejaron de editar acá (el dueño las
     considera casi fijas — solo aparecen en el landing y en la ficha de
     contrato del consultorio). Al no mandarlas, el PATCH las deja intactas
     (`coalesce` en el servidor); para un servicio nuevo simplemente quedan
     vacías hasta que alguien las cargue directo en la base. */
  const mutation = useApiMutation(
    () => service
      ? api.patch(`/admin/services/${service.id}`, {
        name: form.name,
        base_price: priceMinor ?? undefined,
        default_tax_rate: taxRateValid ? taxRate : undefined,
      })
      : api.post('/admin/services', {
        code: form.code, name: form.name,
        base_price: priceMinor ?? undefined,
        default_tax_rate: taxRateValid ? taxRate : undefined,
      }),
    ['/admin/services', '/admin/preview'],
    { onSuccess: onClose },
  );

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal
      title={service ? `Editar ${service.name}` : 'Nuevo servicio'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={!form.name || !priceMinor || !taxRateValid || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Guardar
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}

      {service && (
        <Note tone="info">
          Subir el precio no cambia ningún cobro ya emitido: cada uno guarda el precio que tenía el
          día que se generó. Solo afecta lo que se genere de aquí en adelante.
        </Note>
      )}

      <div className="mt-2">
        {!service && (
          <Field label="Código" required hint="Corto y estable: REGULAR, PREMIUM.">
            <input className="input mono" value={form.code} onChange={set('code')} autoFocus />
          </Field>
        )}
        <Field label="Nombre" required>
          <input className="input" value={form.name} onChange={set('name')} />
        </Field>
        <div className="grid grid--2">
          <Field
            label="Precio (₡)"
            required
            hint={priceMinor ? `Se guarda ${formatMoney(priceMinor)}.` : 'En colones: 65000 o 65.000.'}
          >
            <input
              className="input input--money"
              type="text"
              inputMode="decimal"
              placeholder="₡0"
              value={form.price}
              onChange={set('price')}
            />
          </Field>
          <Field
            label="IVA (%)"
            hint={taxRateValid
              ? `Se guarda IVA del ${form.tax_rate_pct}%.`
              : 'Ej.: 13 para trece por ciento.'}
          >
            <input
              className="input input--money"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              max="99"
              value={form.tax_rate_pct}
              onChange={set('tax_rate_pct')}
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
